// deps
import React, { useContext, useEffect, useMemo, useState } from "react";
import { CheckoutContext, CheckoutRoute } from "@swiggy-private/checkout-core";
import { captureError, GenericError, RouteProps, AsyncComponent } from "@swiggy-private/core-ui";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { clearDineCashPoolingIntent } from "@dineout-cart-client/helpers/DineCashPooling";
import { DineoutSharedCartContext } from "@dineout-cart-client/contexts/DineCashPoolingContext";
import { CheckoutThemeNameContext } from "@dineout-cart-client/components/context-provider";
import { resolveCheckoutTheme } from "@dineout-cart-client/helpers/CheckoutThemes";
import { DineoutPaymentSuccessFlowType } from "@swiggy-private/dineout-payment-success-ui";

interface IProps {
    orderId?: string;
    flowType?: DineoutPaymentSuccessFlowType;
    dealId?: string;
}

async function asyncPromise() {
    try {
        const module = await import("@swiggy-private/dineout-payment-success-ui");
        return module.DineoutPaymentSuccess as React.ComponentType;
    } catch (err) {
        captureError(err);
        return () => <GenericError />;
    }
}

const DineoutPaymentSuccess = (_props: RouteProps<CheckoutRoute>) => {
    const { checkoutState } = useContext(CheckoutContext);
    const { setUserIntent } = useContext(DineoutSharedCartContext);
    const checkoutThemeName = useContext(CheckoutThemeNameContext);
    const props = {
        orderId: (_props?.data as IProps)?.orderId || (checkoutState.payload as IProps)?.orderId,
        flowType: (_props?.data as IProps)?.flowType,
        dealId: (_props?.data as IProps)?.dealId,
    };

    const [colorMode, setColorMode] = useState<"dark" | "light" | undefined>(undefined);

    useEffect(() => {
        if (document.getElementsByTagName("html")[0].classList.contains("dark")) {
            setColorMode("dark");
            return;
        }
        if (document.getElementsByTagName("html")[0].classList.contains("light")) {
            setColorMode("light");
            return;
        }
    }, []);

    useEffect(() => {
        clearDineCashPoolingIntent();
        setUserIntent(false);
    }, [setUserIntent]);

    const content = <AsyncComponent resolve={asyncPromise} {...props} />;

    const themeDescriptor = useMemo(
        () => resolveCheckoutTheme(checkoutThemeName),
        [checkoutThemeName],
    );
    const effectiveColorMode = themeDescriptor.forcedColorMode ?? colorMode;

    return (
        <ThemeProvider theme={themeDescriptor.theme} colorMode={effectiveColorMode}>
            {content}
        </ThemeProvider>
    );
};

export default DineoutPaymentSuccess;
